import { useEffect, useState } from "react";

import { usePortStatusQuery } from "~/hooks/usePortStatus";

import { BinAnimationCategory } from "./BinState";

/**
 * @returns The current bin animation category or null
 * @returns The current bin activity state
 * @returns The number of bins above
 */
export const useBinAnimation = (portId: number, portSideIndex?: number) => {
  const [animationCategory, setAnimationCategory] =
    useState<BinAnimationCategory | null>(null);

  const {
    binState,
    activityState,
    selectedBin,
    isReady,
    isBinOpen,
    inGrid,
    onRobot,
    inPort
  } = usePortStatusQuery(portId, portSideIndex);
  const binMode = binState?.binState.binMode;
  const binPortId = binState?.binState.portId;
  const binsAbove = binState?.binsAbove;

  const isAtAnotherPortModes = binMode === "O" || binMode === "P";
  const isCorrectPortId = binPortId === portId;

  const isOpenAtAnotherPort =
    isAtAnotherPortModes && binPortId !== 0 && !isCorrectPortId;
  const isOpen = isReady || isBinOpen;

  useEffect(() => {
    if (isOpen || !binState) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setAnimationCategory(null);
    } else if (isOpenAtAnotherPort) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setAnimationCategory("Other Port");
    } else if (inGrid) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setAnimationCategory("In Grid");
    } else if (onRobot) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setAnimationCategory("On Robot");
    } else if (inPort) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setAnimationCategory("In Port");
    }
  }, [inGrid, inPort, isOpen, isOpenAtAnotherPort, onRobot, binState]);

  // If the bin is physically open but port status hasn't caught up, force "Bin Opened"
  const effectiveActivityState =
    isOpen && activityState === "Waiting for Bin"
      ? "Bin Opened"
      : activityState;

  return {
    animationCategory,
    activityState: effectiveActivityState,
    binsAbove,
    selectedBin
  };
};
